import type { CSSProperties, FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, useTheme } from '@mui/material'
import { Highlight, themes } from 'prism-react-renderer'
import { CopyButton } from '@/common/components'
import { Prism } from '../utils/prism-languages'

interface ToolCodeBlockProps {
  readonly code: string
  readonly language: string
  readonly showLineNumbers?: boolean
  readonly diffPrefix?: '+' | '-' | null
  readonly hideContinuationBackslashes?: boolean
}

export const ToolCodeBlock: FC<ToolCodeBlockProps> = ({
  code,
  language,
  showLineNumbers = true,
  diffPrefix = null,
  hideContinuationBackslashes = false,
}) => {
  const theme = useTheme()

  return (
    <Box sx={containerSx}>
      <Box className="copy-button" sx={copyButtonWrapperSx}>
        <CopyButton
          text={code}
          variant="overlay"
          tooltipText="Copy code"
          sx={copyButtonPositionSx(theme)}
        />
      </Box>
      <Highlight
        theme={theme.palette.mode === 'dark' ? themes.vsDark : themes.vsLight}
        code={code}
        language={language}
        prism={Prism}
      >
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <Box component="pre" sx={preElementSx(style)}>
            {tokens.map((line, i) => {
              const lineContent = line.map((t) => t.content).join('')
              const isLastLine = i === tokens.length - 1

              return (
                <Box
                  key={`code-${i}-${lineContent.slice(0, 20)}`}
                  {...getLineProps({ line })}
                  sx={diffPrefix ? lineWithDiffPrefixSx(theme, diffPrefix) : lineSx}
                >
                  {showLineNumbers && (
                    <Box component="span" sx={lineNumberSx(theme)}>
                      {String(i + 1).padStart(4, ' ')}
                    </Box>
                  )}
                  <Box component="span" sx={lineContentSx}>
                    {diffPrefix && (
                      <Box component="span" sx={diffPrefixElementSx(theme, diffPrefix)}>
                        {diffPrefix}
                      </Box>
                    )}
                    {line.map((token, tokenIdx) => {
                      const isLastToken = tokenIdx === line.length - 1
                      const isContinuationBackslash =
                        hideContinuationBackslashes &&
                        !isLastLine &&
                        isLastToken &&
                        token.content.trim() === '\\'

                      const tokenProps = getTokenProps({ token })
                      return (
                        <Box
                          component="span"
                          key={`token-${i}-${tokenIdx}-${token.content.slice(0, 10)}`}
                          {...tokenProps}
                          sx={isContinuationBackslash ? hiddenBackslashSx : {}}
                        />
                      )
                    })}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </Highlight>
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  position: 'relative',
  '&:hover .copy-button': {
    opacity: 1,
  },
}

const copyButtonWrapperSx: SxProps<Theme> = {
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
}

const copyButtonPositionSx = (theme: Theme): SxProps<Theme> => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 1,
})

const preElementSx = (style: CSSProperties): SxProps<Theme> => ({
  ...(style as Record<string, unknown>),
  margin: 0,
  padding: 0,
  backgroundColor: 'transparent',
  overflow: 'auto',
  fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, monospace',
  fontSize: 'inherit',
  lineHeight: 1.5,
})

const lineSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
}

const lineWithDiffPrefixSx = (theme: Theme, prefix: '+' | '-'): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark'
  const addedColor = isDark ? 'rgba(46, 160, 67, 0.15)' : 'rgba(46, 160, 67, 0.12)'
  const removedColor = isDark ? 'rgba(248, 81, 73, 0.15)' : 'rgba(248, 81, 73, 0.12)'
  const backgroundColor = prefix === '+' ? addedColor : removedColor

  return {
    display: 'flex',
    alignItems: 'center',
    backgroundColor,
  }
}

const lineNumberSx = (theme: Theme): SxProps<Theme> => ({
  display: 'inline-block',
  width: 48,
  px: 1,
  textAlign: 'right',
  color: theme.palette.text.disabled,
  userSelect: 'none',
  flexShrink: 0,
  fontSize: '0.75em',
})

const lineContentSx: SxProps<Theme> = {
  flex: 1,
  px: 1,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  overflowX: 'hidden',
}

const diffPrefixElementSx = (theme: Theme, prefix: '+' | '-'): SxProps<Theme> => ({
  display: 'inline-block',
  width: 16,
  textAlign: 'center',
  color: prefix === '+' ? theme.palette.success.main : theme.palette.error.main,
  fontWeight: 'bold',
  userSelect: 'none',
  mr: 0.5,
})

const hiddenBackslashSx: SxProps<Theme> = {
  opacity: 0.05,
  userSelect: 'text',
}
